import { useEffect, useRef, type ReactNode } from 'react'
import type { HealthState } from '../lib/api'
import { rssiBars, rssiDbm } from '../lib/format'

/* --------------------------------------------------------------- primitives */

export function Card({
  title,
  actions,
  children,
  flush,
}: {
  title?: ReactNode
  actions?: ReactNode
  children: ReactNode
  flush?: boolean
}) {
  return (
    <section className="card">
      {(title || actions) && (
        <header className="card-head">
          <span>{title}</span>
          {actions}
        </header>
      )}
      <div className={flush ? 'card-body flush' : 'card-body'}>{children}</div>
    </section>
  )
}

export function Stat({
  label,
  value,
  foot,
  tone,
}: {
  label: string
  value: ReactNode
  foot?: ReactNode
  tone?: 'ok' | 'warn' | 'danger'
}) {
  const colour = tone ? `rgb(var(--${tone === 'ok' ? 'ok' : tone}))` : undefined
  return (
    <section className="card stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color: colour }}>
        {value}
      </div>
      {foot && <div className="stat-foot">{foot}</div>}
    </section>
  )
}

export function Pill({
  tone = 'idle',
  children,
  pulse,
}: {
  tone?: 'ok' | 'warn' | 'danger' | 'info' | 'idle'
  children: ReactNode
  pulse?: boolean
}) {
  return (
    <span className={`pill ${tone}`}>
      <i className={pulse ? 'dot pulse' : 'dot'} />
      {children}
    </span>
  )
}

export function Empty({ title, hint }: { title: string; hint?: ReactNode }) {
  return (
    <div className="empty">
      <div className="empty-title">{title}</div>
      {hint && <div className="small">{hint}</div>}
    </div>
  )
}

export function Banner({
  tone = 'info',
  children,
}: {
  tone?: 'ok' | 'warn' | 'danger' | 'info'
  children: ReactNode
}) {
  return <div className={`banner ${tone}`}>{children}</div>
}

export function Loading({ rows = 5 }: { rows?: number }) {
  return (
    <div className="stack" style={{ gap: 9, padding: 16 }}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton" style={{ width: `${94 - i * 7}%` }} />
      ))}
    </div>
  )
}

/* --------------------------------------------------------------- indicators */

const HEALTH_TONE: Record<HealthState, 'ok' | 'warn' | 'danger' | 'idle'> = {
  Healthy: 'ok',
  Degraded: 'warn',
  Dark: 'danger',
  Unknown: 'idle',
}

export function HealthPill({ state, reason }: { state: HealthState; reason?: string | null }) {
  return (
    <Pill tone={HEALTH_TONE[state]} pulse={state === 'Healthy'}>
      {state.toLowerCase()}
      {state === 'Dark' && reason ? ` · ${reason}` : ''}
    </Pill>
  )
}

/** Delivery status colouring. Submitted is deliberately "ok" but not "delivered". */
export function StatusPill({ status }: { status: string }) {
  const tone =
    status === 'Delivered'
      ? 'ok'
      : status === 'Submitted'
        ? 'info'
        : status === 'Failed' || status === 'Undelivered' || status === 'Expired'
          ? 'danger'
          : status === 'Parked' || status === 'Unknown'
            ? 'warn'
            : 'idle'
  return <Pill tone={tone}>{status.toLowerCase()}</Pill>
}

export function SignalBars({ rssi }: { rssi?: number | null }) {
  const bars = rssiBars(rssi)
  const dbm = rssiDbm(rssi)
  const colour = bars >= 3 ? 'var(--ok)' : bars === 2 ? 'var(--warn)' : 'var(--danger)'
  return (
    <span
      className="row"
      style={{ gap: 7, color: bars === 0 ? 'rgb(var(--faint))' : `rgb(${colour})` }}
      title={dbm === null ? 'No signal reading' : `${rssi} (${dbm} dBm)`}
    >
      <span className="bars">
        {[1, 2, 3, 4].map((n) => (
          <i key={n} className={n <= bars ? 'on' : ''} />
        ))}
      </span>
      <span className="num small">{rssi === 99 || rssi === null || rssi === undefined ? '—' : rssi}</span>
    </span>
  )
}

export function Spark({ points }: { points: number[] }) {
  const max = Math.max(1, ...points)
  return (
    <div className="spark">
      {points.map((p, i) => (
        <i key={i} style={{ height: `${Math.max(2, (p / max) * 100)}%` }} title={`${p}`} />
      ))}
    </div>
  )
}

/** A group's colour token rendered as a chip, so pools are recognisable across pages. */
export function GroupChip({ name, colour }: { name: string; colour?: string | null }) {
  const swatch = colour || 'var(--faint)'
  return (
    <span className="chip">
      <i className="dot" style={{ background: swatch.startsWith('#') ? swatch : `rgb(var(--glow))` }} />
      {name}
    </span>
  )
}

/* -------------------------------------------------------------------- modal */

export function Modal({
  title,
  children,
  footer,
  onClose,
  wide,
}: {
  title: ReactNode
  children: ReactNode
  footer?: ReactNode
  onClose: () => void
  wide?: boolean
}) {
  const ref = useRef<HTMLDivElement>(null)

  // Escape closes, and focus moves into the dialog so keyboard users are not left behind on the
  // page underneath.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    ref.current?.querySelector<HTMLElement>('input, select, textarea, button')?.focus()
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={wide ? 'modal wide' : 'modal'} ref={ref} role="dialog" aria-modal="true">
        <header className="modal-head">
          <h2>{title}</h2>
          <button className="btn ghost sm" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>
        <div className="modal-body">{children}</div>
        {footer && <footer className="modal-foot">{footer}</footer>}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------- fields */

export function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: ReactNode
  children: ReactNode
}) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  )
}

export function Check({
  checked,
  onChange,
  label,
  hint,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  label: string
  hint?: string
}) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label className="check">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        {label}
      </label>
      {hint && <div className="field-hint" style={{ marginLeft: 24 }}>{hint}</div>}
    </div>
  )
}

/** Shown once, never retrievable: API keys and temporary passwords. */
export function SecretReveal({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="stack" style={{ gap: 8 }}>
      <div className="field-label">{label}</div>
      <div className="secret">{value}</div>
      <div className="row" style={{ gap: 8 }}>
        <button className="btn sm" onClick={() => navigator.clipboard?.writeText(value)}>
          Copy
        </button>
        <span className="field-hint" style={{ margin: 0 }}>
          {note ?? 'Copy it now — this is the only time it is shown.'}
        </span>
      </div>
    </div>
  )
}
